import {
	MutationCache,
	type MutationObserver,
	QueryCache,
	QueryClient,
} from "@tanstack/react-query";
import { reportAppError } from "./telemetry";

export const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: (error) => reportAppError(error, "query"),
	}),
	mutationCache: new MutationCache({
		onError: (error) => reportAppError(error, "mutation"),
	}),
	defaultOptions: {
		queries: {
			retry: 0,
			staleTime: 1 * 60 * 1000,
		},
		mutations: {
			retry: 0,
		},
	},
});

export function waitForMutation<TData>(
	mutationObserver: MutationObserver<TData>,
): Promise<TData> {
	return new Promise((resolve, reject) => {
		mutationObserver.subscribe((res) => {
			if (res.isError) {
				reject(res.error);
				return;
			}
			if (res.isSuccess) {
				resolve(res.data);
				return;
			}
		});
	});
}

export function cleanQuery<T extends Record<string, any>>(q: T): Partial<T> {
	const out: Record<string, any> = {};

	for (const [k, v] of Object.entries(q)) {
		if (v === undefined || v === null) continue;

		if (typeof v === "string") {
			const s = v.trim();
			if (s === "") continue;
			out[k] = s;
			continue;
		}

		if (Array.isArray(v)) {
			if (v.length === 0) continue;
			out[k] = v;
			continue;
		}

		out[k] = v;
	}

	return out as Partial<T>;
}

export function omit<T extends Record<string, any>, K extends keyof T>(
	obj: T,
	keys: readonly K[],
): Omit<T, K> {
	const out = { ...obj };
	for (const k of keys) delete (out as any)[k];
	return out;
}
